<template>
  <div class="ai-assistant">
    <div v-if="!aiAvailable" class="ai-alert">
      <i class="el-icon-warning-outline"></i>
      <span>AI 服务当前不可用，请稍后重试</span>
    </div>

    <div class="ai-assistant-body">
      <conversation-list
        v-show="!listCollapsed"
        :sessions="sessions"
        :active-id="sessionId"
        :loading="listLoading"
        @select="openSession"
        @create="createSession"
      />

      <chat-panel
        :mode="mode"
        :messages="messages"
        :sending="sending"
        :title="currentTitle"
        :collapsed="listCollapsed"
        :disabled="!aiAvailable || rateLimited"
        @send="onSend"
        @resend="onResend"
        @update:mode="onChangeMode"
        @attach="onAttach"
        @toggle-collapse="listCollapsed = !listCollapsed"
      />
    </div>
  </div>
</template>

<script>
import ConversationList from "./components/ConversationList.vue";
import ChatPanel from "./components/ChatPanel.vue";
import aiApi, { MODE_LABEL } from "./api/aiApi";

const KEY_MARKER = "ai_marker_user";
const keyCurrent = (uid) => `ai_current_${uid}`;
const keyModeMap = (uid) => `ai_session_mode_${uid}`;

// 上游 429 后冻结发送入口的时长（设计文档 §6 承诺 2 秒）
const RATE_LIMIT_COOLDOWN = 2000;

export default {
  name: "Assistant",
  components: { ConversationList, ChatPanel },
  data() {
    return {
      sessions: [],
      messages: [],
      sessionId: "",
      mode: "consult",
      sending: false,
      listLoading: false,
      listCollapsed: false,
      aiAvailable: true,
      // 刚被上游限流：短暂冻结发送入口
      rateLimited: false,
      rateLimitTimer: null,
      seq: 0,
      gen: 0,
    };
  },
  computed: {
    userId() {
      return (this.$store.state.user && this.$store.state.user._id) || "";
    },
    currentTitle() {
      const hit = this.sessions.find((s) => s.sessionId === this.sessionId);
      return hit ? hit.topic || "新对话" : "新对话";
    },
  },
  created() {
    this.bootstrap();
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    // 冷却期内被销毁时要清掉定时器，否则回调会去动已销毁组件的状态
    if (this.rateLimitTimer) clearTimeout(this.rateLimitTimer);
  },
  methods: {
    // ---------- 本地 UI 状态 ----------
    resetLocalStateIfUserChanged() {
      const uid = this.userId;
      if (localStorage.getItem(KEY_MARKER) !== uid) {
        Object.keys(localStorage)
          .filter((k) => k.indexOf("ai_") === 0)
          .forEach((k) => localStorage.removeItem(k));
        localStorage.setItem(KEY_MARKER, uid);
      }
    },
    readModeMap() {
      try {
        const v = JSON.parse(localStorage.getItem(keyModeMap(this.userId)) || "{}");
        return v && typeof v === "object" && !Array.isArray(v) ? v : {};
      } catch (e) {
        return {};
      }
    },
    writeModeMap(map) {
      localStorage.setItem(keyModeMap(this.userId), JSON.stringify(map));
    },
    rememberSession(sessionId) {
      if (!sessionId) return;
      const map = this.readModeMap();
      map[sessionId] = this.mode;
      this.writeModeMap(map);
      localStorage.setItem(
        keyCurrent(this.userId),
        JSON.stringify({ sessionId, mode: this.mode })
      );
    },
    /** 作废在途请求：其回调此后不再写入任何状态 */
    invalidateInflight() {
      this.gen += 1;
      this.sending = false;
    },

    // ---------- 启动 ----------
    async bootstrap() {
      this.resetLocalStateIfUserChanged();
      await this.loadSessions();

      let restored = null;
      try {
        restored = JSON.parse(localStorage.getItem(keyCurrent(this.userId)) || "null");
      } catch (e) {
        restored = null;
      }

      if (restored && restored.sessionId && this.sessions.some((s) => s.sessionId === restored.sessionId)) {
        this.mode = restored.mode || "consult";
        await this.openSession(restored.sessionId);
      } else if (this.sessions.length) {
        await this.openSession(this.sessions[0].sessionId);
      }

      // 不可用时显示常驻告警条，并禁用整个输入区（见设计文档第 6 章）
      this.aiAvailable = await aiApi.health();
      if (!this.aiAvailable) {
        this.$message.warning("AI 服务当前不可用，请稍后重试");
      }
    },

    async loadSessions() {
      this.listLoading = true;
      try {
        this.sessions = await aiApi.listSessions();
      } catch (e) {
        this.sessions = [];
      } finally {
        this.listLoading = false;
      }
    },

    // ---------- 会话 ----------
    async openSession(sessionId) {
      if (!sessionId) return;
      const gen = ++this.gen;
      this.sending = false;
      try {
        const data = await aiApi.getHistory(sessionId);
        if (gen !== this.gen) return;
        this.sessionId = sessionId;
        const list = Array.isArray(data && data.messages) ? data.messages : [];
        this.messages = list.map((m) => ({ ...m, _key: this.nextKey() }));
        // 会话归属的模式决定当前模式
        const map = this.readModeMap();
        if (map[sessionId]) this.mode = map[sessionId];
        localStorage.setItem(
          keyCurrent(this.userId),
          JSON.stringify({ sessionId, mode: this.mode })
        );
      } catch (e) {
        if (gen !== this.gen) return;
        // 会话已被上游清理
        this.sessions = this.sessions.filter((s) => s.sessionId !== sessionId);
        if (this.sessionId === sessionId) {
          this.sessionId = "";
          this.messages = [];
        }
        this.$message.error("该会话已不存在");
      }
    },

    createSession() {
      this.invalidateInflight();
      this.sessionId = "";
      this.messages = [];
    },

    onChangeMode(next) {
      if (next === this.mode) return;
      const label = MODE_LABEL[next] || next;
      this.$confirm(`切换到「${label}」将开启新会话，当前对话会保留在历史列表中。`, "切换模式", {
        confirmButtonText: "确定切换",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.mode = next;
          this.createSession();
        })
        .catch(() => {});
    },

    // ---------- 发送 ----------
    /**
     * 所有发送路径（输入区、空状态建议问句、错误气泡上的重试）共用的闸门：
     * 输入区已按 disabled 置灰，但建议问句与重试按钮不经过输入区，必须各自过这道判断，
     * 否则服务不可用或刚被限流时点它们会发出一个注定失败的请求。
     */
    canSendNow() {
      if (this.rateLimited) {
        this.$message.warning("请求过于频繁，请稍后重试");
        return false;
      }
      if (!this.aiAvailable) {
        this.$message.warning("AI 服务当前不可用，请稍后重试");
        return false;
      }
      return true;
    },

    async onSend({ input }) {
      const text = (input || "").trim();
      if (!this.canSendNow()) return;
      if (!text || this.sending) return;

      const gen = ++this.gen; // 本次请求的代际
      this.messages.push({ _key: this.nextKey(), role: "user", content: text, timestamp: this.now() });
      const placeholderKey = this.nextKey();
      this.messages.push({ _key: placeholderKey, role: "ai", content: "", pending: true });
      this.sending = true;
      await this.runChat({ gen, placeholderKey, text });
    },

    /**
     * 错误气泡上的「重试」：该轮用户消息与上游记录都已存在，因此只把这条错误原地换成
     * 新的占位，不再追加用户消息；重发沿用当前 sessionId（上游在异常时会回滚未完成的
     * 消息写入，重发不会产生重复记录）。
     */
    async onResend(message) {
      const key = message && message._key;
      const text = (message && message.retryInput) || "";
      if (!this.canSendNow()) return;
      if (!key || !text || this.sending) return;

      const gen = ++this.gen;
      this.replacePlaceholder(key, { _key: key, role: "ai", content: "", pending: true });
      this.sending = true;
      await this.runChat({ gen, placeholderKey: key, text });
    },

    /**
     * 发一次对话并落占位。代际判定集中在这里，onSend 与 onResend 共用：
     * 回调晚于后续请求到达时一律不写状态，且必须把本次自己的占位清掉 ——
     * 否则「正在输入…」三点动画会永久转圈，正是这个设计要避免的「看起来卡死」。
     */
    async runChat({ gen, placeholderKey, text }) {
      const requestedSessionId = this.sessionId || null;
      try {
        const res = await aiApi.chat({ input: text, mode: this.mode, sessionId: requestedSessionId });
        // 期间已切换会话或模式：本地丢弃回复（上游已持久化，重开该会话即可看到）
        if (gen !== this.gen) {
          this.dropPlaceholder(placeholderKey);
          return;
        }
        this.replacePlaceholder(placeholderKey, {
          _key: placeholderKey,
          role: "ai",
          content: res.response || "（未返回内容）",
          timestamp: this.now(),
        });

        if (res.sessionId) {
          const rotated = requestedSessionId && res.sessionId !== requestedSessionId;
          this.sessionId = res.sessionId;
          this.rememberSession(res.sessionId);
          await this.loadSessions();
          if (rotated) this.$message.info("已开启新会话");
        }
      } catch (e) {
        // 同上：过期的失败不再写状态，也不弹过期错误，但占位同样要清掉
        if (gen !== this.gen) {
          this.dropPlaceholder(placeholderKey);
          return;
        }
        // 后端 DTO 校验失败时 message 是数组（全局 ValidationPipe 的返回形态），需要归一成字符串
        const raw = e && e.response && e.response.data && e.response.data.message;
        const msg = (Array.isArray(raw) ? raw.join("；") : raw) || "发送失败，请重试";
        // retryInput 供错误气泡上的「重试」原样重发，用户不必重新输入
        this.replacePlaceholder(placeholderKey, {
          _key: placeholderKey,
          role: "ai",
          content: msg,
          error: true,
          retryInput: text,
        });
        if (e && e.response && e.response.status === 429) {
          this.$message.warning("请求过于频繁，请稍后重试");
          this.startRateLimitCooldown();
        }
      } finally {
        // 只有仍然当代的请求才有权复位 sending，否则会清掉后续请求的状态
        if (gen === this.gen) this.sending = false;
      }
    },

    /** 被上游限流后冻结发送入口 2 秒（设计文档 §6） */
    startRateLimitCooldown() {
      if (this.rateLimitTimer) clearTimeout(this.rateLimitTimer);
      this.rateLimited = true;
      this.rateLimitTimer = setTimeout(() => {
        this.rateLimitTimer = null;
        this.rateLimited = false;
      }, RATE_LIMIT_COOLDOWN);
    },

    replacePlaceholder(key, next) {
      const i = this.messages.findIndex((m) => m._key === key);
      // $set 在数组上是 splice：既触发重渲染，也触发 ChatPanel 对 messages 的浅层 watch
      if (i !== -1) this.$set(this.messages, i, next);
    },

    dropPlaceholder(key) {
      const i = this.messages.findIndex((m) => m._key === key);
      // 同样用 splice 就地增删，保持与 replacePlaceholder 一致的触发方式
      if (i !== -1) this.messages.splice(i, 1);
    },

    onAttach() {
      this.$message.info("附件上传功能开发中");
    },

    // ---------- 工具 ----------
    nextKey() {
      this.seq += 1;
      return `m-${Date.now()}-${this.seq}`;
    },
    now() {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
    onResize() {
      this.listCollapsed = window.innerWidth <= 900;
    },
  },
};
</script>

<style scoped>
.ai-assistant {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  overflow: hidden;
}

.ai-assistant-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

/* AI 服务不可用时的常驻告警条 */
.ai-alert {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 13px;
  border-bottom: 1px solid #faecd8;
}
</style>
